import { ErrorHandler, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './public/login/login.component';
import { ForgetComponent } from './public/forget/forget.component';
import { ResetComponent } from './public/reset/reset.component';
import { MainComponent } from './private/layout/main/main.component';
import { SharedModule } from './private/shared/shared.module';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { HttpResponseInterceptor } from './utils/HttpResponseInterceptor';
import { ToastrModule } from 'ngx-toastr';
import { GlobalErrorHandler } from './utils/GlobalErrorHandler';
import { ButtonComponent } from './components/button/button.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import {
  MatDatepickerModule,
  MatRangeDateSelectionModel,
} from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSortModule } from '@angular/material/sort';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { AsyncPipe, CommonModule } from '@angular/common';
import { EventsComponent } from './private/modules/events/events.component';
import { CreateEventComponent } from './private/modules/components/create-event/create-event.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ClickOutsideDirective } from './click-outside.directive';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { WhatsappTemplateComponent } from './private/modules/configuration/whatsapp-template/whatsapp-template.component';
import { CreataeWhatsappTemplateComponent } from './private/modules/components/creatae-whatsapp-template/creatae-whatsapp-template.component';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { SafeUrlPipe } from './safe-url.pipe';

import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { EmailTemplateComponent } from './private/modules/configuration/email-template/email-template.component';
import { EmailEditorModule } from 'angular-email-editor';
import { CreateEmailTemplateComponent } from './private/modules/components/create-email-template/create-email-template.component';
import { ContactGroupsComponent } from './private/modules/configuration/contact-groups/contact-groups.component';
import { CreateContactGroupComponent } from './private/modules/components/create-contact-group/create-contact-group.component';
import { AddContactsGroupComponent } from './private/modules/components/add-contacts-group/add-contacts-group.component';
import { MatTabsModule } from '@angular/material/tabs';
import { BroadcastCampaignComponent } from './private/modules/configuration/broadcast-campaign/broadcast-campaign.component';
import { CreateBroadcastCampaignComponent } from './private/modules/components/create-broadcast-campaign/create-broadcast-campaign.component';
import { AiAgentFlowsComponent } from './private/modules/configuration/ai-agent-flows/ai-agent-flows.component';
import { CreateAiAgentFlowComponent } from './private/modules/configuration/ai-agent-flows/create-ai-agent-flow/create-ai-agent-flow.component';
import { WorkflowAutomationComponent } from './private/modules/automation/workflow-automation/workflow-automation.component';
import { WorkflowBuilderComponent } from './private/modules/automation/workflow-automation/workflow-builder.component';
import { FFlowModule } from '@foblex/flow';
import { MatRadioModule } from '@angular/material/radio';
import { ContactGroupListComponent } from './private/modules/components/contact-group-list/contact-group-list.component';

import { CsvDropzoneComponent } from './private/modules/components/csv-dropzone/csv-dropzone.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ManageAccountFieldComponent } from './private/modules/configuration/manage-account-field/manage-account-field.component';
import { ManageContactFieldComponent } from './private/modules/configuration/manage-contact-field/manage-contact-field.component';
import { EncryptionInterceptor } from 'src/app/encryption-interceptor.service';
import { LearningComponent } from './private/modules/learning/learning.component';
import { OnboardingComponent } from './public/onboarding/onboarding.component';
import { CustomerDataDeletionComponent } from './public/customer-data-deletion/customer-data-deletion.component';
import { ChatEmbedComponent } from './public/chat-embed/chat-embed.component';
import { CacheInterceptor } from './utils/cache.interceptor';
const materialModules = [
  FormsModule,
  ReactiveFormsModule,
  MatButtonModule,
  MatCheckboxModule,
  NgbModule,
  ToastrModule.forRoot({
    timeOut: 1500,
    positionClass: 'toast-bottom-right',
    preventDuplicates: true,
  }),
  MatFormFieldModule,
  MatChipsModule,
  MatAutocompleteModule,
  MatInputModule,
  MatSidenavModule,
  MatDividerModule,
  MatToolbarModule,
  MatIconModule,
  MatMenuModule,
  MatListModule,
  MatCardModule,
  MatTableModule,
  MatPaginatorModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatSortModule,
  AsyncPipe,
  MatProgressSpinnerModule,
  MatSelectModule,
  MatProgressBarModule,
  MatTabsModule,
  MatRadioModule,
  DragDropModule,
  MatSlideToggleModule,
];

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ForgetComponent,
    ResetComponent,
    MainComponent,
    ButtonComponent,
    LearningComponent,
    OnboardingComponent,
    CustomerDataDeletionComponent,
    ChatEmbedComponent,
    AiAgentFlowsComponent,
    CreateAiAgentFlowComponent,
    WorkflowAutomationComponent,
    WorkflowBuilderComponent,
  ],
  imports: [
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    BrowserAnimationsModule,
    materialModules,
    CKEditorModule,
    EmailEditorModule,
    NgxDropzoneModule,
    FFlowModule,
  ],
  schemas: [NO_ERRORS_SCHEMA],
  providers: [
    MatPaginatorModule,
    NgbModule,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // CacheInterceptor MUST come before EncryptionInterceptor:
    //  - sees the original (unencrypted) request body → correct cache keys
    //  - serves cached responses already in decrypted form
    CacheInterceptor,
    { provide: HTTP_INTERCEPTORS, useExisting: CacheInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EncryptionInterceptor,
      multi: true,
    },
    MatRangeDateSelectionModel,
    MatNativeDateModule,
    MatDatepickerModule,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
